import React, { useEffect, useState, useCallback } from 'react'

import { AvailabilityFilter } from './filters/AvailabilityFilter'
import { DateFilter } from './filters/DateFilter'
import { ExperienceFilter } from './filters/ExperienceFilter'
import { TagFilter } from './filters/TagFilter'
import { ExtrasFilter } from './filters/ExtrasFilter'
import { WorkStatusFilters } from './filters/WorkStatusFilters'
import { AvailableCommitmentsFilter } from './filters/AvailableCommitmentsFilter'
import { DiversityFilter } from './filters/DiversityFilter'
import { EmploymentStatusFilter } from './filters/EmploymentStatusFilter'
import { DemographicFilter } from './filters/DemographicFilter'
import { UnionFilter } from './filters/UnionFilter'
import { AlumniFilter } from './filters/AlumniFilter'
import { CityProvinceFilter } from './filters/CityProvinceFilter'
import { UnionStatusSwitch } from './filters/UnionStatusSwitch'
import { CityFilter } from './filters/CityFilter'
import ShareDialog from 'components/ShareDialog'
import { createPublicGroup } from 'utils'

import {
  Box,
  VerticalDivider,
  Typography,
  ExperienceIcon,
  TagIcon,
  StarBorderIcon,
  Button,
} from 'components'
import ShareIcon from '@material-ui/icons/Share'

import { useHasActiveSubscription, useIsMobile, useOnboardConfig } from 'utils'
import pluralize from 'pluralize'
import { format } from 'date-fns'
import {
  FilterMenuButton,
  DesktopFilters,
  MobileFilters,
  MobileFilterMenu,
} from '.'
// Using simplified CityFilter component that directly updates filter state
import { CityFilterButton } from './CityFilterButton'
import {
  AVAILABLE_FROM_CALENDAR,
  GENDER_TAGS_ENABLED,
  DIVERSITY_TAGS_ENABLED,
  COMMITMENT_STATUS,
  ONTARIO_WORKING_STATUS,
  ONTARIO_RESIDENCE_STATUS,
  USE_DEMO_LANDER,
  DISABLE_AVAILABILITY,
  DISABLE_CRRAL_CALENDAR
} from 'constants'

export const FilterBar = ({
  user,
  filterState,
  setFilterState,
  cities,
  titles,
  skills,
  renderedUsers,
  filterConfig = {
    isEnabled: () => true, // Default to showing all filters if config not provided
    getLabel: (id, defaultLabel) => defaultLabel // Default to using the original label
  },
  setSnackMessage,
}) => {
  // State for share dialog
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareExpiration, setShareExpiration] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [tempFilterState, setTempFilterState] = useState(filterState)
  const [showReset, setShowReset] = useState(false)
  const hasActiveSubscription = useHasActiveSubscription(user?.id)
  const isMobile = useIsMobile()

  // Get onboarding config in parent component
  const onboardConfig = useOnboardConfig()

  // No longer using onboarding steps to control filter visibility
  // Filter visibility is now entirely controlled by the market filters configuration

  // Calculate if any filters are active
  const dateChanged = +filterInitialState.date1 !== +filterState.date1 && filterState.date1;
  const availChanged = Object.entries(filterInitialState.status || {}).some(
    ([k, v]) => filterState.status && filterState.status[k] !== v
  );
  const experienceChanged = Object.entries(filterInitialState.experience || {}).some(
    ([k, v]) => filterState.experience && filterState.experience[k] !== v
  );
  const workStatusChanged = filterState.work_status || filterState.resident || filterState.relocate;
  const roleChanged = !!filterState.role;
  const skillChanged = filterState.skills && filterState.skills.length > 0;
  const locationChanged = !!filterState.city;
  const availableCommitmentsChanged = filterState.available_commitments && 
    Object.keys(filterState.available_commitments).length < 3;
  const residentChanged = !!filterState.ontario_resident;
  const relocateChanged = !!filterState.relocate_status;
  const diversityChanged = 
    (filterState.gender_tags && filterState.gender_tags.length > 0) || 
    (filterState.diversity_tags && filterState.diversity_tags.length > 0);
  const demographicChanged = filterState.disabilityStatus === 'Yes' || 
    (filterState.selectedDisabilities && filterState.selectedDisabilities.length > 0) ||
    (filterState.selfIdentificationTags && filterState.selfIdentificationTags.length > 0);
  const unionChanged = 
    (filterState.selectedUnions && filterState.selectedUnions.length > 0) || 
    filterState.union_status === true;
  const alumniChanged = filterState.programs && filterState.programs.length > 0;
  const cityProvinceChanged = filterState.market && filterState.market.cityId;
  const employmentStatusChanged = filterState.employment_status && 
    filterState.employment_status.length < 3;
  const cityFilterChanged = !!filterState.cityFilter;
  
  // Define anyFiltersActive based on all the filter conditions
  const anyFiltersActive = 
    locationChanged ||
    skillChanged ||
    roleChanged ||
    experienceChanged ||
    availChanged ||
    dateChanged ||
    cityFilterChanged ||
    employmentStatusChanged ||
    (ONTARIO_WORKING_STATUS && workStatusChanged) ||
    (ONTARIO_RESIDENCE_STATUS && residentChanged) ||
    (ONTARIO_RESIDENCE_STATUS && relocateChanged) ||
    (COMMITMENT_STATUS && availableCommitmentsChanged) ||
    (DIVERSITY_TAGS_ENABLED && diversityChanged) ||
    demographicChanged ||
    unionChanged ||
    alumniChanged ||
    cityProvinceChanged;

  useEffect(() => {
    setTempFilterState(() => ({ ...filterState }))
  }, [filterState])

  // Handle share search button click
  const handleShareSearch = async () => {
    try {
      // Show loading state
      setIsSharing(true);
      
      if (!renderedUsers || renderedUsers.length === 0) {
        setSnackMessage('No freelancers to share in the current search.');
        return;
      }
      
      // Create a clean version of filterState by removing undefined properties
      const cleanFilterState = JSON.parse(JSON.stringify(filterState || {}));
      
      // Save to publicGroups collection
      const { id, expirationDate } = await createPublicGroup(renderedUsers, cleanFilterState);
      
      // Generate shareable link
      const shareableLink = `${window.location.origin}/shared/${id}`;
      
      // Save link and expiration for dialog
      setShareLink(shareableLink);
      setShareExpiration(expirationDate);
      setShareDialogOpen(true);
      
      // Copy to clipboard automatically
      try {
        await navigator.clipboard.writeText(shareableLink);
      } catch (clipboardError) {
        console.error('Could not copy to clipboard:', clipboardError);
        // We don't show an error here since the dialog will let them copy manually
      }
      
    } catch (error) {
      console.error('Error sharing search:', error);
      setSnackMessage('Error creating shareable link. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  let heading = 'Search Filters'

  if (filterState.role) {
    heading = `${pluralize(filterState.role)}`
    if (filterState.city) {
      heading = `${pluralize(filterState.role)} in ${cities[filterState.city]}`
    }
  } else if (filterState.city) {
    heading = `Freelancers in ${cities[filterState.city]}`
  }

  const resetState = (...keys) => {
    if (keys.length === 0) {
      keys = Object.keys(filterInitialState)
    }
    keys.forEach((key) => {
      setTempFilterState((fs) => ({ ...fs, [key]: filterInitialState[key] }))
      setFilterState((fs) => ({ ...fs, [key]: filterInitialState[key] }))
    })
  }

  return (
    <Box display="flex" alignItems="center" mt={3.5} mb={2}>
      <Typography variant="h5">{heading}</Typography>
      <VerticalDivider mx={3} />
      <Box display="flex" flex={1} justifyContent="space-between" alignItems="center">
        <Box>
          {isMobile ? (
            <MarketsMobileFilters
              cities={cities}
              tempFilterState={tempFilterState}
              setTempFilterState={setTempFilterState}
              resetState={resetState}
              titles={titles}
              skills={skills}
              setFilterState={setFilterState}
              hasActiveSubscription={hasActiveSubscription}
              filterConfig={filterConfig}
              anyFiltersActive={anyFiltersActive}
              handleShareSearch={handleShareSearch}
              isSharing={isSharing}
              setSnackMessage={setSnackMessage}
              renderedUsers={renderedUsers}
            />
          ) : (
            <MarketsDesktopFilters
              cities={cities}
              filterState={filterState}
              setFilterState={setFilterState}
              tempFilterState={tempFilterState}
              setTempFilterState={setTempFilterState}
              resetState={resetState}
              hasActiveSubscription={hasActiveSubscription}
              renderedUsers={renderedUsers}
              filterConfig={filterConfig}
              anyFiltersActive={anyFiltersActive}
              handleShareSearch={handleShareSearch}
              isSharing={isSharing}
            />
          )}
        </Box>

        {!isMobile && filterConfig.isEnabled('union_status') && (
          <Box ml={3}>
            <UnionStatusSwitch
              filterState={tempFilterState}
              setFilterState={setTempFilterState}
            />
          </Box>
        )}
        
        {/* Share Dialog */}
        <ShareDialog 
          open={shareDialogOpen} 
          onClose={() => setShareDialogOpen(false)} 
          shareLink={shareLink}
          expirationDate={shareExpiration}
        />
      </Box>
    </Box>
  )
}

const MarketsMobileFilters = ({
  tempFilterState,
  setTempFilterState,
  resetState,
  titles,
  skills,
  cities,
  setFilterState,
  hasActiveSubscription,
  filterConfig = {
    isEnabled: () => true, // Default to showing all filters if config not provided
    getLabel: (id, defaultLabel) => defaultLabel // Default to using the original label
  },
  anyFiltersActive,
  handleShareSearch,
  isSharing,
  setSnackMessage,
  renderedUsers
}) => {
  const [open, setOpen] = useState(false)

  const filterProps = {
    filterState: tempFilterState,
    setFilterState: setTempFilterState,
  }

  return (
    <MobileFilters
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false)
        setFilterState({ ...tempFilterState })
      }}
      onClear={() => {
        resetState()
        setOpen(false)
      }}
    >
      {DISABLE_CRRAL_CALENDAR && filterConfig.isEnabled('date') &&
        <MobileFilterMenu disabled={!hasActiveSubscription} label={filterConfig.getLabel('date', 'Dates')}>
          <DateFilter {...filterProps} />
        </MobileFilterMenu>}
      {DISABLE_AVAILABILITY && filterConfig.isEnabled('availability') &&
        <MobileFilterMenu disabled={!hasActiveSubscription} label={filterConfig.getLabel('availability', 'Availability')}>
          <AvailabilityFilter {...filterProps} />
        </MobileFilterMenu>}
      {filterConfig.isEnabled('experience') &&
        <MobileFilterMenu disabled={!hasActiveSubscription} label={filterConfig.getLabel('experience', 'Years')}>
          <ExperienceFilter {...filterProps} />
        </MobileFilterMenu>}
      {filterConfig.isEnabled('role') &&
        <MobileFilterMenu disabled={!hasActiveSubscription} label={filterConfig.getLabel('role', 'Role')}>
          <TagFilter
            items={titles}
            onChange={(role) => setFilterState((s) => ({ ...s, role }))}
          />
        </MobileFilterMenu>}
      {filterConfig.isEnabled('location') &&
        <MobileFilterMenu disabled={!hasActiveSubscription} label={filterConfig.getLabel('location', 'Location')}>
          <TagFilter
            items={Object.values(cities)}
            onChange={(t) => {
              const city = Object.entries(cities).find(([k, v]) => v === t)[0]
              setFilterState((s) => ({ ...s, city }))
            }}
          />
        </MobileFilterMenu>}
      {filterConfig.isEnabled('cities') &&
        <MobileFilterMenu disabled={!hasActiveSubscription} label={filterConfig.getLabel('cities', 'City Search')}>
          {/* For mobile, we use tempFilterState and sync to global state on save */}
          <CityFilter
            filterState={tempFilterState}
            setFilterState={(newState) => {
              setTempFilterState(newState);
              setFilterState(newState); // Also update global state immediately
            }}
          />
        </MobileFilterMenu>}
      {filterConfig.isEnabled('skills') &&
        <MobileFilterMenu disabled={!hasActiveSubscription} label={filterConfig.getLabel('skills', 'Skills')}>
          <TagFilter
            items={skills}
            onChange={(skill) =>
              setFilterState((s) => ({ ...s, skills: [...s.skills, skill] }))
            }
          />
        </MobileFilterMenu>}
      {filterConfig.isEnabled('demographic') && (
        <MobileFilterMenu disabled={!hasActiveSubscription} label={filterConfig.getLabel('demographic', 'Demographics')}>
          <DemographicFilter
            filterState={tempFilterState}
            setFilterState={setTempFilterState}
          />
        </MobileFilterMenu>
      )}
      {filterConfig.isEnabled('union') && (
        <MobileFilterMenu disabled={!hasActiveSubscription} label={filterConfig.getLabel('union', 'Union')}>
          <UnionFilter
            filterState={tempFilterState}
            setFilterState={setTempFilterState}
          />
        </MobileFilterMenu>
      )}

      {filterConfig.isEnabled('union_status') && (
        <MobileFilterMenu disabled={!hasActiveSubscription} label={filterConfig.getLabel('union_status', 'Union Status')}>
          <UnionStatusSwitch
            filterState={tempFilterState}
            setFilterState={setTempFilterState}
          />
        </MobileFilterMenu>
      )}
      
      <MobileFilterMenu 
        label="Actions"
        disabled={!anyFiltersActive || !hasActiveSubscription}
      >
        <Box display="flex" flexDirection="column" width="100%" p={1}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleShareSearch}
            disabled={isSharing || !anyFiltersActive}
            startIcon={<ShareIcon />}
            fullWidth
            style={{ marginBottom: 8 }}
          >
            {isSharing ? 'Creating link...' : 'Share this search'}
          </Button>
          
          <Button 
            variant="outlined"
            color="primary" 
            onClick={() => resetState()}
            fullWidth
          >
            Reset all filters
          </Button>
        </Box>
      </MobileFilterMenu>
      {filterConfig.isEnabled('alumni') && (
        <MobileFilterMenu disabled={!hasActiveSubscription} label={filterConfig.getLabel('alumni', 'Alumni')}>
          <AlumniFilter
            filterState={tempFilterState}
            setFilterState={setTempFilterState}
          />
        </MobileFilterMenu>
      )}
      {filterConfig.isEnabled('city_province') && (
        <MobileFilterMenu disabled={!hasActiveSubscription} label={filterConfig.getLabel('city_province', 'City/Province')}>
          <CityProvinceFilter
            filterState={tempFilterState}
            setFilterState={setTempFilterState}
          />
        </MobileFilterMenu>
      )}
    </MobileFilters>
  )
}

const MarketsDesktopFilters = ({
  filterState,
  setFilterState,
  tempFilterState,
  setTempFilterState,
  resetState,
  cities,
  hasActiveSubscription,
  renderedUsers = [],
  filterConfig = {
    isEnabled: () => true, // Default to showing all filters if config not provided
    getLabel: (id, defaultLabel) => defaultLabel // Default to using the original label
  },
  anyFiltersActive,
  handleShareSearch,
  isSharing
}) => {

  const dateChanged =
    +filterInitialState.date1 !== +filterState.date1 && filterState.date1

  const availChanged = Object.entries(filterInitialState.status).some(
    ([k, v]) => filterState.status[k] !== v,
  )

  const experienceChanged = Object.entries(filterInitialState.experience).some(
    ([k, v]) => filterState.experience[k] !== v,
  )

  const workStatusChanged = filterState.work_status || filterState.resident || filterState.relocate
  const miscChanged = filterState.favorites || filterState.has_equipment
  const roleChanged = !!filterState.role
  const skillChanged = filterState.skills.length > 0
  const locationChanged = !!filterState.city
  const availableCommitmentsChanged = Object.keys(filterState.available_commitments).length < 3
  const residentChanged = !!filterState.ontario_resident
  const relocateChanged = !!filterState.relocate_status
  const diversityChanged = !!filterState.gender_tags.length > 0 || !!filterState.diversity_tags.length > 0
  const demographicChanged = filterState.disabilityStatus === 'Yes' || 
    (filterState.selectedDisabilities && filterState.selectedDisabilities.length > 0) ||
    (filterState.selfIdentificationTags && filterState.selfIdentificationTags.length > 0)
  const unionChanged = (filterState.selectedUnions && filterState.selectedUnions.length > 0) || filterState.union_status === true
  const alumniChanged = filterState.programs && filterState.programs.length > 0
  const cityProvinceChanged = filterState.market && filterState.market.cityId
  const employmentStatusChanged = filterState.employment_status.length < 3
  const cityFilterChanged = !!filterState.cityFilter

  // We're now using the anyFiltersActive prop passed from the parent component
  // No need to recalculate it here

  const menuProps = {
    onSave: () => setFilterState({ ...tempFilterState }),
    onCancel: () => setTempFilterState({ ...filterState }),
  }
  const filterProps = {
    filterState: tempFilterState,
    setFilterState: setTempFilterState,
  }

  // We're using the anyFiltersActive passed from the parent component
  // No need to recalculate it here

  return (
    <DesktopFilters 
      allowReset={anyFiltersActive} 
      resetState={resetState} 
      renderedUsers={renderedUsers} 
      allowMessage={anyFiltersActive}
      allowShare={anyFiltersActive && hasActiveSubscription}
      onShareClick={handleShareSearch}
      isSharing={isSharing}
    >
      {!DISABLE_CRRAL_CALENDAR && filterConfig.isEnabled('date') &&
        <FilterMenuButton
          disabled={!hasActiveSubscription}
          icon="Dates"
          label={
            dateChanged
              ? filterState.date2
                ? `${format(filterState.date1, 'MMM d')} - ${format(
                  filterState.date2,
                  'MMM d',
                )}`
                : format(filterState.date1, 'MMM d')
              : filterConfig.getLabel('date', 'Dates')
          }
          changed={dateChanged}
          onReset={() => resetState('date1', 'date2')}
          {...menuProps}
        >
          <DateFilter {...filterProps} />
        </FilterMenuButton>}
      {!DISABLE_AVAILABILITY && filterConfig.isEnabled('availability') &&
        <>
          {
            AVAILABLE_FROM_CALENDAR ? (
              <FilterMenuButton
                disabled={!hasActiveSubscription}
                icon={availChanged ? null : 'Availability'}
                customIcon={
                  availChanged ? <AvailIcon status={filterState.status} /> : null
                }
                label={filterConfig.getLabel('availability', 'Availability')}
                changed={availChanged}
                onReset={() => resetState('status')}
                {...menuProps}
              >
                <AvailabilityFilter {...filterProps} />
              </FilterMenuButton>
            ) :
              <FilterMenuButton
                disabled={!hasActiveSubscription}
                icon={availChanged ? null : 'Availability'}
                customIcon={
                  availChanged ? <AvailIcon status={filterState.status} /> : null
                }
                label={filterConfig.getLabel('availability', 'Availability')}
                changed={availableCommitmentsChanged}
                onReset={() => resetState('available_commitments')}
                {...menuProps}
              >
                <AvailableCommitmentsFilter {...filterProps} />
              </FilterMenuButton>
          }
        </>
      }
      {filterConfig.isEnabled('experience') &&
        <FilterMenuButton
          disabled={!hasActiveSubscription}
          icon="Years"
          label={
            experienceChanged
              ? `${filterState.experience[0]}-${filterState.experience[1]} Years`
              : filterConfig.getLabel('experience', 'Years')
          }
          Icon={ExperienceIcon}
          changed={experienceChanged}
          onReset={() => resetState('experience')}
          {...menuProps}
        >
          <ExperienceFilter {...filterProps} />
        </FilterMenuButton>
      }
      {filterConfig.isEnabled('cities') &&
        <CityFilterButton
          disabled={!hasActiveSubscription}
          icon="location"
          label={filterState.cityFilter ? filterState.cityFilter.label : filterConfig.getLabel('cities', 'City Search')}
          changed={!!filterState.cityFilter}
          onReset={() => resetState('cityFilter')}
          {...menuProps}
        >
          {/* Direct access to global filter state */}
          <CityFilter 
            filterState={filterState}
            setFilterState={setFilterState}
          />
        </CityFilterButton>
      }
      {filterConfig.isEnabled('extras') &&
        <FilterMenuButton
          disabled={!hasActiveSubscription}
          label={filterConfig.getLabel('extras', 'Extras')}
          Icon={StarBorderIcon}
          changed={miscChanged}
          onReset={() => {
            resetState('favorites')
            resetState('has_equipment')
          }}
          {...menuProps}
        >
          <ExtrasFilter {...filterProps} />
        </FilterMenuButton>
      }
      {
        USE_DEMO_LANDER && filterConfig.isEnabled('employment_status') && (
          <FilterMenuButton
            disabled={!hasActiveSubscription}
            label={filterConfig.getLabel('employment_status', 'Status')}
            changed={employmentStatusChanged}
            onReset={() => resetState('employment_status')}
            {...menuProps}
          >
            <EmploymentStatusFilter {...filterProps} />
          </FilterMenuButton>
        )
      }
      {
        ONTARIO_WORKING_STATUS && filterConfig.isEnabled('work_status') && (
          <FilterMenuButton
            label={filterConfig.getLabel('work_status', 'Work Status')}
            changed={workStatusChanged || residentChanged || relocateChanged}
            onReset={() => {
              resetState('work_status')
              resetState('ontario_resident')
              resetState('relocate')
            }}
            {...menuProps}
          >
            <WorkStatusFilters {...filterProps} />
          </FilterMenuButton>
        )
      }
      {
        (GENDER_TAGS_ENABLED || DIVERSITY_TAGS_ENABLED) && filterConfig.isEnabled('diversity') && (
          <FilterMenuButton
            label={`${GENDER_TAGS_ENABLED && 'Gender'} ${GENDER_TAGS_ENABLED && DIVERSITY_TAGS_ENABLED && ' + '} ${DIVERSITY_TAGS_ENABLED && 'Diversity'}`}
            changed={diversityChanged}
            onReset={() => resetState('gender_tags', 'diversity_tags')}
            {...menuProps}
          >
            <DiversityFilter {...filterProps} />
          </FilterMenuButton>
        )
      }
      {
        filterConfig.isEnabled('demographic') && (
          <FilterMenuButton
            label={filterConfig.getLabel('demographic', 'Demographics')}
            changed={demographicChanged}
            onReset={() => {
              resetState('disabilityStatus');
              resetState('selectedDisabilities');
              resetState('selfIdentificationTags');
            }}
            {...menuProps}
          >
            <DemographicFilter {...filterProps} />
          </FilterMenuButton>
        )
      }
      {
        filterConfig.isEnabled('union') && (
          <FilterMenuButton
            label={filterConfig.getLabel('union', 'Union')}
            changed={unionChanged}
            onReset={() => {
              resetState('selectedUnions');
              resetState('unionStatus');
              resetState('union_status');
            }}
            {...menuProps}
          >
            <UnionFilter {...filterProps} />
          </FilterMenuButton>
        )
      }
      {
        filterConfig.isEnabled('alumni') && (
          <FilterMenuButton
            label={filterConfig.getLabel('alumni', 'Alumni')}
            changed={alumniChanged}
            onReset={() => resetState('programs')}
            {...menuProps}
          >
            <AlumniFilter {...filterProps} />
          </FilterMenuButton>
        )
      }
      {
        filterConfig.isEnabled('city_province') && (
          <FilterMenuButton
            label={filterConfig.getLabel('city_province', 'City/Province')}
            changed={cityProvinceChanged}
            onReset={() => resetState('market')}
            {...menuProps}
          >
            <CityProvinceFilter {...filterProps} />
          </FilterMenuButton>
        )
      }
      {
        filterState.role && filterConfig.isEnabled('role') && (
          <FilterMenuButton
            disabled={!hasActiveSubscription}
            label={filterState.role}
            Icon={TagIcon}
            changed={roleChanged}
            onReset={() => resetState('role')}
          />
        )
      }
      {
        filterState.city && filterConfig.isEnabled('location') && (
          <FilterMenuButton
            disabled={!hasActiveSubscription}
            icon="location"
            label={cities[filterState.city]}
            changed={locationChanged}
            {...menuProps}
            onReset={() => resetState('city')}
            Icon={TagIcon}
          />
        )
      }
      {
        filterConfig.isEnabled('skills') && filterState.skills.map((skill) => (
          <FilterMenuButton
            disabled={!hasActiveSubscription}
            icon="skills"
            key={`skill-${skill}`}
            label={skill}
            changed={skillChanged}
            onReset={() => resetState('skills')}
          />
        ))
      }
      {
        filterConfig.isEnabled('diversity') && filterState.gender_tags.map((gender_tag) => (
          <FilterMenuButton
            disabled={!hasActiveSubscription}
            // icon="tag"
            key={`tag-${gender_tag}`}
            label={gender_tag}
            changed={diversityChanged}
            onReset={() => resetState('gender_tags')}
          />
        ))
      }
      {
        filterConfig.isEnabled('diversity') && filterState.diversity_tags.map((diversity_tag) => (
          <FilterMenuButton
            disabled={!hasActiveSubscription}
            // icon="tag"
            key={`tag-${diversity_tag}`}
            label={diversity_tag}
            changed={diversityChanged}
            onReset={() => resetState('diversity_tags')}
          />
        ))
      }
    </DesktopFilters >
  )
}

export const filterInitialState = {
  date1: undefined,
  date2: undefined,
  experience: [1, 20],
  role: undefined,
  skills: [],
  city: undefined,
  favorites: undefined,
  has_equipment: undefined,
  ontario_resident: undefined,
  relocate: undefined,
  status: [1, 1, 1],
  permit_status: undefined,
  availability_status: undefined,
  available_commitments: { fullTime: true, partTime: true, freelance: true },
  gender_tags: [],
  diversity_tags: [],
  // Updated property names to match onboarding data
  selfIdentificationTags: [], // For self-identification
  disabilityStatus: 'No',
  selectedDisabilities: [],
  unionStatus: 'Non-Union', // Legacy format
  union_status: false, // New boolean format
  selectedUnions: [],
  programs: [], // For alumni
  market: {}, // For city/province
  employment_status: ['Full Time', 'Contract', 'Freelance'],
  cityFilter: undefined, // For city search filter
}

const AvailIcon = ({ status }) => (
  <div style={{ marginRight: 10, marginBottom: -3 }}>
    {status.map(
      (status, i) =>
        status === 1 && (
          <div
            key={`status-${i}`}
            style={{
              height: 8,
              width: 8,
              borderRadius: 4,
              background: AVAIL_COLORS[i],
              marginRight: 2,
              marginBottom: 3,
            }}
          />
        ),
    )}
  </div>
)
const AVAIL_COLORS = ['#49b592', '#e8d53b', '#f06450']
